import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["src"]
const _hoisted_2 = /*#__PURE__*/_createElementVNode("media-poster", null, null, -1)
const _hoisted_3 = ["src"]
const _hoisted_4 = {
  key: 0,
  class: "video-player-overlay"
}
const _hoisted_5 = { class: "video-player-overlay__content" }
const _hoisted_6 = { class: "chapters" }

import { MediaCommunitySkinElement, MediaPlayerElement, type CommunitySkinTranslations } from "vidstack";
import { VTTCue } from "media-captions";
import { defineCustomElements as customs } from "vidstack/elements";
import { nextTick, ref, watch, shallowReactive } from "vue";

const CHAPTER_TITLE_DELAY = 6000  // 6 seconds

interface Chapter {
  sorting: number;
  cue: VTTCue;
  active: boolean;
  metadata: ChapterMetadata;
}

interface ChapterMetadata {
  thumbnailUrl: string;
  showTitleOnStart: boolean;
  isInitialChapter: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'video-player',
  props: {
    src: {},
    chaptersSrc: {},
    chaptersMetadata: {}
  },
  setup(__props: any) {

const props = __props;

const GERMAN_TRANSLATION: CommunitySkinTranslations = {
  Audio: 'Audio',
  Auto: 'Automatisch',
  Captions: 'Untertitel',
  Chapters: 'Kapitel',
  Default: 'Standard',
  Mute: 'Stumm',
  Normal: 'Normal',
  Off: 'Stopp',
  Pause: 'Pause',
  Play: 'Play',
  Speed: 'Geschwindigkeit',
  Quality: 'Qualität',
  Settings: 'Einstellungen',
  Unmute: 'Stummschaltung aufheben',
  'Seek Forward': 'Vorwärts',
  'Seek Backward': 'Rückwärts',
  'Closed-Captions On': 'Untertitel an',
  'Closed-Captions Off': 'Untertitel aus',
  'Enter Fullscreen': 'Vollbild einschalten',
  'Exit Fullscreen': 'Vollbild ausschalten',
  'Enter PiP': 'Bild-im-Bild einschalten',
  'Exit PiP': 'Bild-im-Bild ausschalten',
};

customs();




const chaptersMetadata: Array<ChapterMetadata> = props.chaptersMetadata && JSON.parse(props.chaptersMetadata) || []
const chapters = ref<Array<Chapter>>([]);

const player = ref<MediaPlayerElement | null>(null);
const skin = ref<MediaCommunitySkinElement | null>(null);

let currentOverlayHeading = ref<string | null>(null)
let currentChapter = ref<Chapter | null>(null)

watch(player, async (player) => {
  await nextTick();

  if (player && chaptersMetadata.length > 0) {
    player.addEventListener("loaded-metadata", () => onTrackUpdates(player));
    player.addEventListener("text-tracks-change", () => onTrackUpdates(player));
  }
});

const humanReadableLength = (chapter: Chapter) => {
  const cueLength = chapter.cue.endTime - chapter.cue.startTime
  return cueLength < 60
    ? "1 min"
    : `${Math.round(cueLength / 60)} min`
}

const onTrackUpdates = (player: MediaPlayerElement) => {
  for (const track of player.textTracks) {
    if (track.kind !== "chapters") continue;

    track.addEventListener("cue-change", () => onActiveCueUpdate(player));
    track.addEventListener("load", () => onTrackLoadUpdate(player));
    track.addEventListener("add-cue", () => onTrackLoadUpdate(player));
    track.addEventListener("remove-cue", () => onTrackLoadUpdate(player));
  }
};

const onTrackLoadUpdate = (player: MediaPlayerElement) => {
  chapters.value = []

  for (const track of player.textTracks) {
    if (track.kind !== "chapters") continue;

    for (const [ indx, cue ] of track.cues.entries()) {
      const newChapter = {
        sorting: indx + 1,
        cue,
        active: false,
        metadata: chaptersMetadata[indx]
      }
      chapters.value.push(newChapter)

      if (newChapter.metadata.isInitialChapter) {
        jumpToChapter(newChapter)
      }
    }
  }

  onActiveCueUpdate(player)
}

const onActiveCueUpdate = (player: MediaPlayerElement) => {
  for (const chapter of chapters.value) chapter.active = false;

  for (const track of player.textTracks) {
    if (track.kind !== "chapters") continue;

    for (const chapter of chapters.value) {
      for (const activeCue of track.activeCues) {
        if (chapter.cue === activeCue) {
          chapter.active = true;
          currentChapter.value = chapter
          updateBreadcrumbs(chapter)
          if (chapter.metadata.showTitleOnStart) {
            showChapterTitle(chapter)
          }
        }
      }
    }
  }
};

const showChapterTitle = (chapter: Chapter) => {
  currentOverlayHeading.value = chapter.cue.text
  setTimeout(() => {
    currentOverlayHeading.value = null
  }, CHAPTER_TITLE_DELAY)
}

const jumpToChapter = (chapter: Chapter) => {
  if(player.value) {
    player.value.currentTime = chapter.cue.startTime;
  }
}

const updateBreadcrumbs = (activeChapter: Chapter) => {
  const lastBreadcrumb = document.querySelector(".layout-breadcrumbs a:last-of-type")
  if (lastBreadcrumb) {
    lastBreadcrumb.textContent = activeChapter.cue.text
    lastBreadcrumb.setAttribute('href', `?clip=${activeChapter.sorting}`)
  }
}

watch(skin, (skin) => {
  if(skin) {
    skin.translations = GERMAN_TRANSLATION;
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(currentChapter))
      ? _renderSlot(_ctx.$slots, "header", {
          key: 0,
          activeChapter: _unref(currentChapter),
          humanReadableLength: () => humanReadableLength(_unref(currentChapter))
        })
      : _createCommentVNode("", true),
    _createElementVNode("div", null, [
      _createElementVNode("media-player", {
        ref_key: "player",
        ref: player,
        src: _ctx.src
      }, [
        _createElementVNode("media-outlet", null, [
          _hoisted_2,
          (_ctx.chaptersSrc)
            ? (_openBlock(), _createElementBlock("track", {
                key: 0,
                src: _ctx.chaptersSrc,
                srclang: "de",
                kind: "chapters",
                default: ""
              }, null, 8, _hoisted_3))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("media-community-skin", {
          ref_key: "skin",
          ref: skin
        }, null, 512),
        (_unref(currentOverlayHeading))
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, _toDisplayString(_unref(currentOverlayHeading)), 1)
            ]))
          : _createCommentVNode("", true)
      ], 8, _hoisted_1)
    ]),
    _renderSlot(_ctx.$slots, "content"),
    _createElementVNode("div", null, [
      _createElementVNode("div", _hoisted_6, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chapters.value, (chapter, indx) => {
          return (_openBlock(), _createElementBlock("div", {
            class: "chapter",
            key: chapter.cue.startTime
          }, [
            _renderSlot(_ctx.$slots, "chapter", {
              chapter: chapter,
              indx: indx,
              jump: () => jumpToChapter(chapter),
              humanReadableLength: () => humanReadableLength(chapter)
            })
          ]))
        }), 128))
      ])
    ])
  ], 64))
}
}

})